const cards = [
  {
    id: "v1",
    suit: "&hearts;",
    suitClass: "red",
    title: "The Recommendation Engineer",
    where: "Amsterdam · 2030",
    body: `
      I'm working on a recommendation system that millions of people use every
      week without ever thinking about it. The team is small, multilingual,
      and obsessed with measuring what actually changes people's behavior
      versus what just looks good in an A/B dashboard. I bike to work.
      I finally know when to use <i>de</i> vs <i>het</i>. Mostly.
    `
  },
  {
    id: "v2",
    suit: "&spades;",
    suitClass: "black",
    title: "The AI / Data Consultant",
    where: "Travelling Europe · 2029",
    body: `
      I spend two-week stints embedded inside companies that have data but no
      idea what to do with it. I help them go from "we have a dashboard
      nobody opens" to "we ship one decision a quarter that actually moves
      a number". I keep a side-project on the train. My French tax forms
      are a nightmare and I wouldn't trade it.
    `
  },
  {
    id: "v3",
    suit: "&diams;",
    suitClass: "red",
    title: "The Researcher Detour",
    where: "A university lab · 2031",
    body: `
      I went back for a PhD because one specific question kept following me
      around: how to make an ethical recommendation system that pushes our products without being over intrusive to the user.
      I publish a few papers, I co-supervise a (I hope a smart and funny) Master's student,
      I still ship side projects on weekends. The pay is worse, the questions
      are better. I'd do it again.
    `
  },
  {
    id: "v4",
    suit: "&clubs;",
    suitClass: "black",
    title: "The Quiet Founder",
    where: "Somewhere far from city",
    body: `
      A side project I built in college kept growing. Now there's three of us,
      a tiny office, and real users paying real money. It's nothing flashy,
      a focused tool that does one thing very well. I do half engineering,
      half talking to users. I'm tired in a good way but also in my own home with a huge garden and a big dog.
    `
  }
];

const cardHTML = (c, idx) => `
  <input type="checkbox" id="${c.id}" class="vis-ck" />
  <label for="${c.id}" class="vis-card vis-card-${idx + 1}">
    <span class="vis-flipper">
      <span class="vis-face vis-back">
        <span class="vis-back-pattern">
          <span class="vis-corner tl">${c.suit}</span>
          <span class="vis-corner br">${c.suit}</span>
          <span class="vis-back-label">click to reveal</span>
        </span>
      </span>
      <span class="vis-face vis-front ${c.suitClass}">
        <span class="vis-front-suit">${c.suit}</span>
        <span class="vis-where">${c.where}</span>
        <span class="vis-front-title">${c.title}</span>
        <span class="vis-body">${c.body}</span>
      </span>
    </span>
  </label>
`;

const checkedSelectors = cards
  .map(c => `.vis-ck#${c.id}:checked + .vis-card .vis-flipper`)
  .join(",\n  ");

const visionHTML = `
<style>
/* Paint the IE wrap div (parent of .vis-wrap) so no white band shows when stretched */
*:has(> .vis-wrap) {
  background: #0a4d2a !important;
  margin: 0 !important;
  display: flex !important;
  flex-direction: column !important;
}
.vis-wrap {
  font-family: "MS Sans Serif", Tahoma, sans-serif;
  /* Casino-felt background: radial vignette over green base, with a faint
     diagonal weave to suggest fabric texture. */
  background-color: #0a4d2a;
  background-image:
    radial-gradient(ellipse at center, #14743f 0%, #0a4d2a 55%, #052816 100%),
    repeating-linear-gradient(45deg,
      rgba(255,255,255,0.025) 0 2px,
      transparent 2px 5px),
    repeating-linear-gradient(-45deg,
      rgba(0,0,0,0.06) 0 2px,
      transparent 2px 5px);
  background-blend-mode: normal, overlay, overlay;
  padding: clamp(14px, 3vw, 30px) clamp(10px, 2.5vw, 28px);
  min-height: 100%;
  flex: 1 1 auto;
  box-sizing: border-box;
  color: #fff;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}
/* Gold double-stitched table border, inset slightly so it frames the felt */
.vis-wrap::before {
  content: "";
  position: absolute;
  inset: 8px;
  border: 2px solid #c9a04a;
  outline: 1px solid rgba(0, 0, 0, 0.4);
  outline-offset: 2px;
  pointer-events: none;
  border-radius: 6px;
  box-shadow:
    inset 0 0 0 1px rgba(255, 215, 130, 0.35),
    inset 0 0 40px rgba(0, 0, 0, 0.45);
}
.vis-wrap h2 {
  font-family: "Comic Sans MS", cursive;
  margin: 0 0 4px 0;
  font-size: clamp(18px, 2.4vw, 26px);
  text-shadow: 1px 1px 0 #000;
}
.vis-wrap .vis-sub {
  font-size: clamp(11px, 1.2vw, 14px);
  margin-bottom: clamp(14px, 2.5vw, 28px);
  font-style: italic;
  opacity: 0.9;
}
.vis-deck {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: clamp(10px, 1.6vw, 22px);
  justify-items: center;
  align-items: start;
  perspective: 1400px;
  width: 100%;
  max-width: 1100px;
  flex: 1 0 auto;
}
.vis-ck { display: none; }
.vis-card {
  width: 100%;
  max-width: 240px;
  aspect-ratio: 5 / 7;
  cursor: pointer;
  display: block;
  transform-origin: center center;
  animation-duration: 0.6s;
  animation-timing-function: cubic-bezier(.18,.7,.3,1.15);
  animation-fill-mode: backwards;
}
.vis-card-1 { animation-name: vis-deal-1; animation-delay: 0.45s; }
.vis-card-2 { animation-name: vis-deal-2; animation-delay: 0.60s; }
.vis-card-3 { animation-name: vis-deal-3; animation-delay: 0.75s; }
.vis-card-4 { animation-name: vis-deal-4; animation-delay: 0.90s; }

/* Each card flies out from a central stacked spot into its grid slot.
   Starting transforms are big enough to overshoot the grid offset and converge near center. */
@keyframes vis-deal-1 {
  0%   { transform: translate(180%, -30%) rotate(-14deg) scale(0.78); opacity: 0; }
  35%  { opacity: 1; }
  70%  { transform: translate(0, 0) rotate(2deg) scale(1.02); }
  100% { transform: none; opacity: 1; }
}
@keyframes vis-deal-2 {
  0%   { transform: translate(60%, -30%) rotate(-6deg) scale(0.78); opacity: 0; }
  35%  { opacity: 1; }
  70%  { transform: translate(0, 0) rotate(-2deg) scale(1.02); }
  100% { transform: none; opacity: 1; }
}
@keyframes vis-deal-3 {
  0%   { transform: translate(-60%, -30%) rotate(8deg) scale(0.78); opacity: 0; }
  35%  { opacity: 1; }
  70%  { transform: translate(0, 0) rotate(-3deg) scale(1.02); }
  100% { transform: none; opacity: 1; }
}
@keyframes vis-deal-4 {
  0%   { transform: translate(-180%, -30%) rotate(14deg) scale(0.78); opacity: 0; }
  35%  { opacity: 1; }
  70%  { transform: translate(0, 0) rotate(3deg) scale(1.02); }
  100% { transform: none; opacity: 1; }
}

/* Subtle pre-deal "shuffle" ripple: header bobs while the imaginary deck shuffles */
.vis-wrap h2,
.vis-wrap .vis-sub {
  animation: vis-bob 0.45s ease-in-out;
}
@keyframes vis-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-3px); }
}

@media (prefers-reduced-motion: reduce) {
  .vis-card,
  .vis-wrap h2,
  .vis-wrap .vis-sub { animation: none !important; }
}
@supports not (aspect-ratio: 1) {
  .vis-card { height: 280px; }
}
.vis-flipper {
  display: block;
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.7s cubic-bezier(.4,.2,.2,1);
  transform-style: preserve-3d;
}
${checkedSelectors} {
  transform: rotateY(180deg);
}
.vis-face {
  position: absolute;
  inset: 0;
  border-radius: 8px;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  box-shadow: 3px 3px 0 rgba(0,0,0,0.35);
  display: flex;
  flex-direction: column;
  padding: 14px;
  box-sizing: border-box;
  text-align: center;
}
.vis-back {
  background:
    repeating-linear-gradient(45deg, #800020 0 8px, #a3002a 8px 16px),
    #800020;
  border: 4px solid #fff;
  color: #fff;
}
.vis-back-pattern {
  flex: 1;
  border: 1px dashed rgba(255,255,255,0.6);
  border-radius: 4px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Comic Sans MS", cursive;
}
.vis-corner {
  position: absolute;
  font-size: 22px;
  color: #fff;
}
.vis-corner.tl { top: 6px; left: 8px; }
.vis-corner.br { bottom: 6px; right: 8px; transform: rotate(180deg); }
.vis-back-label {
  font-size: 13px;
  opacity: 0.85;
  letter-spacing: 1px;
}
.vis-front {
  background: #fff;
  color: #000;
  border: 4px solid #fff;
  transform: rotateY(180deg);
  text-align: left;
  line-height: 1.4;
  overflow: hidden;
}
.vis-front.red { color: #b00020; }
.vis-front.black { color: #111; }
.vis-front-suit {
  font-size: clamp(16px, 2.2cqi, 24px);
  align-self: flex-start;
}
.vis-where {
  font-size: clamp(8px, 1.2cqi, 11px);
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #555;
  margin-top: 2px;
}
.vis-front-title {
  font-family: "Comic Sans MS", cursive;
  font-size: clamp(12px, 1.7cqi, 17px);
  font-weight: bold;
  margin: 4px 0 6px 0;
  color: inherit;
}
.vis-body {
  color: #222;
  font-size: clamp(10px, 1.3cqi, 13px);
  line-height: 1.45;
}
.vis-card { container-type: inline-size; }
.vis-foot {
  margin-top: clamp(14px, 2.4vw, 26px);
  font-size: clamp(10px, 1.1vw, 13px);
  font-style: italic;
  opacity: 0.85;
  max-width: 720px;
}
</style>

<div class="vis-wrap">
  <h2>Where do I see myself in 3 to 5 years?</h2>
  <div class="vis-sub">Pick a card. (You can flip more than one — I haven't decided either.)</div>
  <div class="vis-deck">
    ${cards.map(cardHTML).join("\n")}
  </div>
  <div class="vis-foot">
    The honest answer is "probably some mix of these". But the thread between
    all of them is the same: real problems, useful work, people I respect.
  </div>
</div>
`;

export default visionHTML;
